/**
 * UrgentAttentionPanel - THE work card. Three columns, five rows each.
 *
 * It is now the only place on the dashboard that shows matters, tasks and
 * projects. There used to be three standalone list widgets below it —
 * "Matters Recently Worked On", "Upcoming Tasks", "My Projects" - showing the
 * same records from the same slice in a different shape. Two views of one thing
 * on one page is not redundancy you can style your way out of: whichever one
 * the user reads, the other is wasted vertical space and a second thing to keep
 * consistent. They are gone; this stayed, because three columns side by side
 * COMPARE, where three stacked lists merely repeat.
 *
 * WHAT THAT MADE THIS CARD RESPONSIBLE FOR. It is no longer a supplementary
 * "here is what is on fire" panel that may reasonably render nothing:
 *
 *   • it renders even when every column is empty (the registry's `requires` is
 *     the licence check alone now), because a dashboard whose only work card
 *     disappears is a dashboard a new account cannot start from;
 *   • every column shows an `EmptyState` instead of vanishing, so the row keeps
 *     its three columns and does not reflow as records come and go;
 *   • the hard `.slice(0, 5)` is gone. It predated the column having its own
 *     scroller, and it threw away rows the backend had already fetched and
 *     sent (it returns up to 8 tasks, 8 project deadlines, 5 matters). A tall
 *     card now fills with as many as fit and scrolls for the rest; a short one
 *     scrolls sooner. The header count was always the full number, so it stays
 *     honest either way;
 *   • every column carries BOTH ways out - "View All" in the header for the
 *     full list, and a "+ New …" footer straight into that entity's create
 *     form. Creating was previously only reachable from the widgets that were
 *     removed with the duplicates.
 */

import React from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { completeTask } from '../../../store/actions/TasksActions';
import { completeDashboardTask } from '../../../store/actions/DashboardActions';
import Badge from '../../shared/TailwindComponents/Badge';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import Button from '../../shared/TailwindComponents/Button';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import GlassCard from '../../shared/brand/GlassCard';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { DashboardUrgentTask, DashboardUrgentMatter, DashboardUrgentProject } from '../../../store/reducer/DashboardReducer';
import { formatRelativeTime } from '../../shared/feed/feedTime';
import { parseServerDate } from '../../../utils/functions';
import {
  PiCheckLight,
  PiCalendarLight,
  PiPlusLight,
  PiCheckSquareOffsetLight,
  PiBriefcaseLight,
  PiProjectorScreenChartLight,
  PiLinkSimpleLight,
  PiClockLight,
  PiArchiveLight,
  PiCalendarCheckLight,
  PiCheckCircleLight,
  PiFlagPennantLight,
  PiHourglassMediumLight,
  PiPauseCircleLight,
  PiSealCheckLight,
  PiSpinnerGapLight,
  PiWarningCircleLight,
} from 'react-icons/pi';

interface UrgentAttentionPanelProps {
  urgentTasks: DashboardUrgentTask[];
  urgentMatters: DashboardUrgentMatter[];
  urgentProjects: DashboardUrgentProject[];
}

const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getPriorityColor = (priority: string): BadgeColor => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'gray';
    default: return 'gray';
  }
};

const getDueDateInfo = (dueDate: string | null): { text: string; className: string } => {
  if (!dueDate) return { text: '', className: 'text-gray-400' };
  const due = parseServerDate(dueDate);
  if (!due) return { text: '', className: 'text-gray-400' };
  const diffDays = Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return {
    text: `${Math.abs(diffDays)}d overdue`,
    className: 'text-danger-600 dark:text-danger-400 font-medium',
  };
  if (diffDays === 0) return {
    text: 'Due today',
    className: 'text-warning-600 dark:text-warning-400 font-medium',
  };
  if (diffDays === 1) return {
    text: 'Due tomorrow',
    className: 'text-warning-500 dark:text-warning-400',
  };
  if (diffDays <= 3) return {
    text: `Due in ${diffDays}d`,
    className: 'text-warning-500 dark:text-warning-400',
  };
  return {
    text: `Due in ${diffDays}d`,
    className: 'text-gray-500 dark:text-gray-400',
  };
};


/* ------------------------------------------------------------------ */
/*  Row glyphs                                                         */
/* ------------------------------------------------------------------ */

/**
 * A ROW ICON MUST SAY SOMETHING THE HEADER DOES NOT.
 *
 * Every project row used to carry `PiProjectorScreenChartLight` - the Projects
 * column's own header glyph - and every matter row `PiBriefcaseLight`, the
 * Matters header's. In a column where the heading has already established what
 * kind of thing these are, repeating it on each row is a column of identical
 * marks: it costs a gutter of horizontal space and returns nothing, and it is
 * why the two columns read as interchangeable at a glance.
 *
 * So the glyph carries the thing the header CANNOT know - the row's own state.
 * Projects and tasks are scanned by DEADLINE, so theirs is the urgency of the
 * due date; matters are scanned by STATUS, so theirs is the lifecycle stage.
 * Colour comes from the same reading, and only where it is information.
 */
interface RowGlyph {
  icon: React.ReactNode;
  className: string;
  /** Already-translated, for the icon's `title` - the state said out loud. */
  title: string;
}

/**
 * Deadline urgency: overdue → today → soon → scheduled → undated.
 *
 * The panel already computes the same buckets for the countdown text
 * (`getDueDateInfo`); this is that reading expressed as a mark, so a row is
 * scannable without reading the line under it.
 */
const dueGlyph = (
  dueDate: string | null,
  t: (key: string, options?: any) => any
): RowGlyph => {
  if (!dueDate) {
    return {
      icon: <PiFlagPennantLight />,
      className: 'text-gray-400 dark:text-gray-500',
      title: String(t('No due date')),
    };
  }
  const due = parseServerDate(dueDate);
  if (!due) {
    return {
      icon: <PiFlagPennantLight />,
      className: 'text-gray-400 dark:text-gray-500',
      title: String(t('No due date')),
    };
  }
  const diffDays = Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      icon: <PiWarningCircleLight />,
      className: 'text-danger-500 dark:text-danger-400',
      title: String(t('Overdue')),
    };
  }
  if (diffDays === 0) {
    return {
      icon: <PiHourglassMediumLight />,
      className: 'text-warning-600 dark:text-warning-400',
      title: String(t('Due today')),
    };
  }
  if (diffDays <= 3) {
    return {
      icon: <PiClockLight />,
      className: 'text-warning-500 dark:text-warning-400',
      title: String(t('Due soon')),
    };
  }
  return {
    icon: <PiCalendarCheckLight />,
    className: 'text-gray-500 dark:text-gray-400',
    title: String(t('Scheduled')),
  };
};

/** Matter lifecycle: where in its life this record is. */
const matterGlyph = (
  status: string | null | undefined,
  processing: string | null | undefined,
  t: (key: string, options?: any) => any
): RowGlyph => {
  // Still being ingested: that outranks the status, because nothing else about
  // the matter is trustworthy until it finishes.
  if (processing && ['processing', 'pending', 'in_progress'].includes(processing.toLowerCase())) {
    return {
      icon: <PiSpinnerGapLight />,
      className: 'text-info-500 dark:text-info-400',
      title: String(t('Still processing')),
    };
  }
  switch ((status || '').toLowerCase()) {
    case 'closed':
    case 'completed':
      return {
        icon: <PiSealCheckLight />,
        className: 'text-success-600 dark:text-success-400',
        title: String(t('Closed')),
      };
    case 'archived':
      return {
        icon: <PiArchiveLight />,
        className: 'text-gray-400 dark:text-gray-500',
        title: String(t('Archived')),
      };
    case 'on_hold':
    case 'paused':
      return {
        icon: <PiPauseCircleLight />,
        className: 'text-warning-600 dark:text-warning-400',
        title: String(t('On hold')),
      };
    case 'pending':
      return {
        icon: <PiHourglassMediumLight />,
        className: 'text-warning-500 dark:text-warning-400',
        title: String(t('Pending')),
      };
    default:
      return {
        icon: <PiCheckCircleLight />,
        className: 'text-info-500 dark:text-info-400',
        title: String(t('Open')),
      };
  }
};

/**
 * "+ New …" at the foot of a column.
 *
 * A ghost row in the column's own rhythm, not a second button in the header:
 * the header already carries a count badge and "View All", and a third control
 * up there turns a heading into a toolbar. Muted until approached, champagne on
 * hover - the accent marks the thing you are about to do, which is the scale
 * champagne is for.
 */
const ColumnCreateAction: React.FC<{
  label: string;
  onClick: () => void;
  'data-testid': string;
}> = ({ label, onClick, 'data-testid': testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex w-full flex-shrink-0 items-center gap-2 border-t border-hairline-soft px-5 py-2.5 text-start text-sm text-gray-400 transition-colors duration-200 ease-slide hover:bg-surface-sunken/60 hover:text-champagne focus:bg-surface-sunken/60 focus:text-champagne focus:outline-none dark:text-gray-500"
  >
    <PiPlusLight aria-hidden className="h-4 w-4 flex-shrink-0" />
    {label}
  </button>
);

const UrgentAttentionPanel: React.FC<UrgentAttentionPanelProps> = ({
  urgentTasks,
  urgentMatters,
  urgentProjects,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const showTasks = hasSectionAccess('tasks');
  const showMatters = hasSectionAccess('matters');
  const showProjects = hasSectionAccess('projects');

  // Licence is the only reason a column is absent now. "Nothing is urgent" is
  // a state this card SHOWS - with a way to create the first one - rather than
  // a reason for the page's only work card to vanish.
  if (!showTasks && !showMatters && !showProjects) return null;

  const handleCompleteTask = async (e: React.MouseEvent, taskUuid: string) => {
    e.stopPropagation();
    dispatch(completeDashboardTask(taskUuid));
    await dispatch(completeTask(taskUuid) as any);
  };

  const visibleColCount = [showProjects, showTasks, showMatters].filter(Boolean).length;
  /*
    THE TOP OF THE HEIGHT CHAIN, and every class in it is load-bearing.

    `DashboardGrid` hands this component a cell sized by the S/M/L floor and
    ceiling and a wrapper that is a flex column with `[&>*]:flex-1` - so this
    grid is a flex item that GROWS into whatever height is on offer. The card
    ships on `lg`, which is `calc(100vh - 14rem)`, so it reaches down the
    screen rather than ending after three rows.

    `min-h-0` is what lets it also SHRINK below its content, which is what
    hands the scrolling to the columns instead of to the page. Then
    `lg:grid-rows-[minmax(0,1fr)]` makes the single row take that full height:
    a grid whose rows are `auto` sizes its row to the tallest COLUMN's content
    and leaves the difference as dead space underneath. `minmax(0,1fr)` and not
    a bare `1fr` - `1fr` carries an `auto` minimum, which is exactly how a grid
    row silently grows past its container and takes the scrollbar with it.
    `h-full` is belt-and-braces for the case where the parent height IS
    definite; the flex fill is what does the work.

    All of it is `lg:`-gated for the rows template because below `lg` the
    columns stack, the cell has no definite height, and each column should be
    as tall as its own content.
  */
  const gridClass = clsx(
    'grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-rows-[minmax(0,1fr)]',
    visibleColCount >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
  );

  return (
    <div className={gridClass}>
      {/* Project Deadlines */}
      {showProjects && (
        <GlassCard
          icon={<PiProjectorScreenChartLight />}
          title={
            <>
              {t('Project Deadlines')}
              <span className="ms-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">
                {urgentProjects.length}
              </span>
            </>
          }
          actions={
            <Button variant="outline" size="small" onClick={() => history.push('/projects')}>
              {t('View All')}
            </Button>
          }
          padding="none"
          className="overflow-hidden"
          /*
            THE MIDDLE OF THE CHAIN. `fill` makes the card `flex h-full
            min-h-0 flex-col` and its header `flex-shrink-0` - that is what
            pins the count and "View All" to the top. `bodyClassName` then
            makes the body the column the list and the footer live in; without
            it the body is content-sized and the list below has no definite
            height to be `flex-1` of.
          */
          fill
          bodyClassName="flex min-h-0 flex-1 flex-col"
        >
          {/*
            THE BOTTOM OF THE CHAIN - the only thing on the card that scrolls.
            `min-h-0` is the whole trick: a flex item's minimum size is its
            content, so without it this box refuses to be shorter than its rows
            and the overflow escapes to the page instead of scrolling here.
            `[&>*]:flex-shrink-0` keeps the rows at their natural height rather
            than letting the column squeeze them to fit.
          */}
          <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto [&>*]:flex-shrink-0">
            {urgentProjects.length === 0 ? (
              <EmptyState
                className="my-auto"
                density="compact"
                icon={<PiProjectorScreenChartLight />}
                headline={t('No project deadlines')}
                subtext={t('Projects with a due date in the next week appear here.')}
              />
            ) : urgentProjects.map((project) => {
              const dueInfo = getDueDateInfo(project.due_date);
              const glyph = dueGlyph(project.due_date, t);
              return (
                <div
                  key={project.uuid}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-surface-sunken/70 cursor-pointer transition-colors"
                  onClick={() => history.push(`/projects/${project.uuid}`)}
                >
                  <span
                    title={glyph.title}
                    aria-label={glyph.title}
                    data-testid="project-row-glyph"
                    className={clsx('flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4', glyph.className)}
                  >
                    {glyph.icon}
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {project.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {dueInfo.text && (
                        <span className={`text-xs flex items-center gap-1 ${dueInfo.className}`}>
                          <PiCalendarLight className="w-3 h-3" />
                          {dueInfo.text}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                        <PiLinkSimpleLight className="w-3 h-3" />
                        {project.resource_count}
                      </span>
                    </div>
                  </div>

                  <Badge color={getPriorityColor(project.priority)}>
                    {capitalize(project.priority)}
                  </Badge>
                </div>
              );
            })}
          </div>
          <ColumnCreateAction
            label={t('New project')}
            onClick={() => history.push('/projects?add=true')}
            data-testid="urgent-new-project"
          />
        </GlassCard>
      )}

      {/* Urgent Tasks */}
      {showTasks && (
        <GlassCard
          icon={<PiCheckSquareOffsetLight />}
          title={
            <>
              {t('Upcoming & Overdue Tasks')}
              {urgentTasks.length > 0 && (
                <span className="ms-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300">
                  {urgentTasks.length}
                </span>
              )}
            </>
          }
          actions={
            <Button variant="outline" size="small" onClick={() => history.push('/tasks')}>
              {t('View All')}
            </Button>
          }
          padding="none"
          className="overflow-hidden"
          /*
            THE MIDDLE OF THE CHAIN. `fill` makes the card `flex h-full
            min-h-0 flex-col` and its header `flex-shrink-0` - that is what
            pins the count and "View All" to the top. `bodyClassName` then
            makes the body the column the list and the footer live in; without
            it the body is content-sized and the list below has no definite
            height to be `flex-1` of.
          */
          fill
          bodyClassName="flex min-h-0 flex-1 flex-col"
        >
          {/*
            THE BOTTOM OF THE CHAIN - the only thing on the card that scrolls.
            `min-h-0` is the whole trick: a flex item's minimum size is its
            content, so without it this box refuses to be shorter than its rows
            and the overflow escapes to the page instead of scrolling here.
            `[&>*]:flex-shrink-0` keeps the rows at their natural height rather
            than letting the column squeeze them to fit.
          */}
          <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto [&>*]:flex-shrink-0">
            {urgentTasks.length === 0 ? (
              <EmptyState
                className="my-auto"
                density="compact"
                icon={<PiCheckSquareOffsetLight />}
                headline={t('No upcoming or overdue tasks')}
                subtext={t('Nothing is due in the next few days — enjoy it.')}
              />
            ) : (
              urgentTasks.map((task) => {
                const dueInfo = getDueDateInfo(task.due_date);
                return (
                  <div
                    key={task.uuid}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-surface-sunken/70 cursor-pointer transition-colors group"
                    onClick={() => history.push(`/tasks/${task.uuid}`)}
                  >
                    {/* Complete button */}
                    <button
                      onClick={(e) => handleCompleteTask(e, task.uuid)}
                      className="flex-shrink-0 w-7 h-7 rounded-full border-2 border-hairline
                        group-hover:border-success-400 group-hover:bg-success-50 dark:group-hover:border-success-500 dark:group-hover:bg-success-900/20
                        flex items-center justify-center transition-all
                        hover:!border-success-500 hover:!bg-success-100 dark:hover:!bg-success-900/40 hover:scale-110"
                      title={t('Complete task')}
                    >
                      <PiCheckLight className="w-3.5 h-3.5 text-gray-400 group-hover:text-success-500 transition-colors" />
                    </button>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {task.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs flex items-center gap-1 ${dueInfo.className}`}>
                          <PiCalendarLight className="w-3 h-3" />
                          {dueInfo.text}
                        </span>
                        {task.matter_title && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[140px]">
                            {task.matter_title}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* The people on it. These rows have carried
                        `collaborators` (with `profile_picture`) and a
                        `collaborator_count` all along and drew none of them —
                        `CollaboratorStack` normalises whichever spelling the
                        endpoint used and renders through `ProfileImage`, so
                        the pictures resolve and initials are the fallback
                        rather than the only state. No access labels: these
                        rows carry no access field to assert one from. */}
                    <CollaboratorStack
                      collaborators={task.collaborators ?? []}
                      totalCount={task.collaborator_count}
                      size="xs"
                      max={3}
                      withAccessLabels={false}
                      testId="dashboard-task-collaborators"
                      className="flex-shrink-0"
                    />
                    <Badge color={getPriorityColor(task.priority)}>
                      {capitalize(task.priority)}
                    </Badge>
                  </div>
                );
              })
            )}
          </div>
          <ColumnCreateAction
            label={t('New task')}
            onClick={() => history.push('/tasks?add=true')}
            data-testid="urgent-new-task"
          />
        </GlassCard>
      )}

      {/* Recently Worked On (Matters) */}
      {showMatters && (
        <GlassCard
          icon={<PiBriefcaseLight />}
          title={
            <>
              {t('Recently Worked On')}
              {urgentMatters.length > 0 && (
                <span className="ms-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-info-100 text-info-700 dark:bg-info-900/40 dark:text-info-300">
                  {urgentMatters.length}
                </span>
              )}
            </>
          }
          actions={
            <Button variant="outline" size="small" onClick={() => history.push('/matters')}>
              {t('View All')}
            </Button>
          }
          padding="none"
          className="overflow-hidden"
          /*
            THE MIDDLE OF THE CHAIN. `fill` makes the card `flex h-full
            min-h-0 flex-col` and its header `flex-shrink-0` - that is what
            pins the count and "View All" to the top. `bodyClassName` then
            makes the body the column the list and the footer live in; without
            it the body is content-sized and the list below has no definite
            height to be `flex-1` of.
          */
          fill
          bodyClassName="flex min-h-0 flex-1 flex-col"
        >
          {/*
            THE BOTTOM OF THE CHAIN - the only thing on the card that scrolls.
            `min-h-0` is the whole trick: a flex item's minimum size is its
            content, so without it this box refuses to be shorter than its rows
            and the overflow escapes to the page instead of scrolling here.
            `[&>*]:flex-shrink-0` keeps the rows at their natural height rather
            than letting the column squeeze them to fit.
          */}
          <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto [&>*]:flex-shrink-0">
            {urgentMatters.length === 0 ? (
              <EmptyState
                className="my-auto"
                density="compact"
                icon={<PiBriefcaseLight />}
                headline={t('No recent matters')}
                subtext={t('Matters you open or edit show up here, newest first.')}
              />
            ) : (
              urgentMatters.map((matter) => {
                const glyph = matterGlyph(matter.status, matter.processing_status, t);
                return (
                <div
                  key={matter.uuid}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-surface-sunken/70 cursor-pointer transition-colors"
                  onClick={() => history.push(`/matters/${matter.uuid}`)}
                >
                  <span
                    title={glyph.title}
                    aria-label={glyph.title}
                    data-testid="matter-row-glyph"
                    className={clsx('flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4', glyph.className)}
                  >
                    {glyph.icon}
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {matter.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {matter.updated_at ? (
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                          <PiClockLight className="w-3 h-3" />
                          {matter.updated_at ? t('Updated {{when}}', { when: formatRelativeTime(matter.updated_at, t) }) : ''}
                        </span>
                      ) : matter.status ? (
                        <Badge color="blue">{capitalize(matter.status)}</Badge>
                      ) : null}
                    </div>
                  </div>

                  {/* Same story as the task rows: the people are in the
                      payload and were never drawn. */}
                  <CollaboratorStack
                    collaborators={matter.collaborators ?? []}
                    totalCount={matter.collaborator_count}
                    size="xs"
                    max={3}
                    withAccessLabels={false}
                    testId="dashboard-matter-collaborators"
                    className="flex-shrink-0"
                  />

                  {matter.status && (
                    <Badge color="blue">{capitalize(matter.status)}</Badge>
                  )}
                </div>
                );
              })
            )}
          </div>
          <ColumnCreateAction
            label={t('New matter')}
            onClick={() => history.push('/matters?add=true')}
            data-testid="urgent-new-matter"
          />
        </GlassCard>
      )}
    </div>
  );
};

export default UrgentAttentionPanel;
